import type { AxiosInstance } from "axios";
import type { ClientDetailsResponse } from "@/features/admin/types/clients.types";
import { apiRoutes } from "@/shared/api/routes";
import { logger } from "@/shared/lib/logger";

export const createClientsApi = (apiClient: AxiosInstance) => ({
  /**
   * Lightweight client fetch used by dropdowns/filters (only id, customer_number, name).
   */
  getClientsForSelect: async (
    page: number,
    limit: number,
    query: unknown[],
  ): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: ["id", "customer_number", "name"],
      query,
      options: {
        timeout: 10,
        offset: page * limit,
        limit,
        order: [
          ["name", "asc_nulls_last"],
          ["id", "asc"],
        ],
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/customer/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load clients for select");
    }
  },

  getClientDetails: async (
    page: number,
    limit: number,
    query: unknown[],
    order: unknown[],
  ): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: [
        "id",
        "customer_number",
        "name",
        "enabled",
        "medicaid_number",
        "medicare_number",
        "ssn_encrypted",
        "date_of_birth",
        "gender",
        "language_code",
        "email",
        "phone_mobile",
        "phone_landline",
        "vehicle_req",
        "pickup_delay",
        "dropoff_delay",
        "last_ride_timestamp",
        "creation_ts",
        "home_street_number",
        "home_street",
        "home_street_suffix",
        "home_city",
        "home_postcode",
        "default_device.id",
        "default_device.name",
        "default_device.code",
        "default_provider.id",
        "home_location.id",
        "selfservice_profile.id",
        "selfservice_profile.status",
      ],
      query,
      options: {
        timeout: 10,
        offset: page * limit,
        limit: limit,
        order,
        calculate_total: true,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/customer/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load client details");
    }
  },

  getClientDetailsAdvanced: async (
    page: number,
    limit: number,
    query: unknown[],
    order: unknown[],
  ): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: [
        "id",
        "customer_number",
        "name",
        "enabled",
        "medicaid_number",
        "medicare_number",
        "ssn_encrypted",
        "date_of_birth",
        "gender",
        "language_code",
        "email",
        "phone_mobile",
        "phone_landline",
        "vehicle_req",
        "pickup_delay",
        "dropoff_delay",
        "last_ride_timestamp",
        "creation_ts",
        "home_street_number",
        "home_street",
        "home_street_suffix",
        "home_city",
        "home_postcode",
        "default_device.id",
        "default_device.name",
        "default_device.code",
        "default_provider.id",
        "home_location.id",
        "selfservice_profile.id",
        "selfservice_profile.status",
      ],
      query,
      options: {
        timeout: 10,
        offset: page * limit,
        limit: limit,
        order,
        calculate_total: true,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(apiRoutes.proxy("support/internal/customer/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load advanced client details");
    }
  },

  getClientDetailsFull: async (
    page: number,
    limit: number,
    query: unknown[],
    order: unknown[],
  ): Promise<ClientDetailsResponse> => {
    // First API call - get client data (same as advanced)
    const requestBody = {
      select: [
        "id",
        "customer_number",
        "name",
        "enabled",
        "medicaid_number",
        "medicare_number",
        "ssn_encrypted",
        "date_of_birth",
        "gender",
        "language_code",
        "email",
        "phone_mobile",
        "phone_landline",
        "vehicle_req",
        "pickup_delay",
        "dropoff_delay",
        "last_ride_timestamp",
        "creation_ts",
        "home_street_number",
        "home_street",
        "home_street_suffix",
        "home_city",
        "home_postcode",
        "default_device.id",
        "default_device.name",
        "default_device.code",
        "default_provider.id",
        "default_provider.name",
        "home_location.id",
        "selfservice_profile.id",
        "selfservice_profile.status",
      ],
      query,
      options: {
        timeout: 10,
        offset: page * limit,
        limit: limit,
        order,
        calculate_total: true,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(apiRoutes.proxy("support/internal/customer/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load full client details");
    }
  },

  getCustomerDecisions: async (customerIds: number[]): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: [
        "id",
        "eligibility_type",
        "eligibility_restriction",
        "eligibility_start",
        "eligibility_end",
        "customer.id",
        "fare_type.id",
        "fare_type.display_name",
        "major_funding_source.id",
        "major_funding_source.name",
      ],
      query: [
        ["in", "customer.id", customerIds],
        ["equal", "valid", true],
      ],
      options: {
        timeout: 10,
        offset: 0,
        limit: 1000,
        order: [
          ["major_funding_source.name", "asc_nulls_last"],
          ["id", "desc"],
        ],
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(apiRoutes.proxy("support/internal/customer_decision/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load customer decisions");
    }
  },

  getClientDetailsById: async (id: string): Promise<ClientDetailsResponse> => {
    // TODO: Extract this select array as CLIENT_DETAILS_BY_ID_SELECT_FIELDS and import it
    // in clientsService.ts to eliminate the duplicated positional mapping.
    // Both arrays MUST stay identical in order and content — a single-file edit that
    // reorders or adds a field without updating the other will silently corrupt all
    // getClientDetailsById field mappings. (See PR SHOULD-FIX 2)
    const requestBody = {
      select: [
        "assistant",
        "combined_weight",
        "comment",
        "creation_agent",
        "creation_source",
        "creation_ts",
        "customer_number",
        "date_of_birth",
        "disability_mental",
        "disability_physical",
        "disability_visual",
        "driver_note",
        "dropoff_delay",
        "email",
        "emergency_address",
        "emergency_contact",
        "emergency_phone_day",
        "emergency_phone_night",
        "enabled",
        "ethnicity",
        "extra1",
        "extra2",
        "extra3",
        "extra4",
        "extra5",
        "extra6",
        "extra7",
        "extra8",
        "extra9",
        "extra10",
        "extra11",
        "extra12",
        "extra13",
        "extra14",
        "extra15",
        "extra16",
        "extra17",
        "extra18",
        "extra19",
        "extra20",
        "extra21",
        "extra22",
        "extra23",
        "extra24",
        "extra25",
        "extra26",
        "extra27",
        "extra28",
        "extra29",
        "extra30",
        "first_name",
        "gender",
        "home_city",
        "home_postcode",
        "home_state",
        "home_street",
        "home_street_number",
        "home_street_suffix",
        "household_ages",
        "household_members",
        "id",
        "income_level",
        "ivr_callout_allowed",
        "ivr_notification_allowed",
        "language_code",
        "last_change_timestamp",
        "last_name",
        "last_ride_timestamp",
        "marital_status",
        "medicaid_number",
        "medicare_number",
        "middle_name_initials",
        "name",
        "no_public_transport",
        "oneclick_validation_agent",
        "oneclick_validation_timestamp",
        "outsource_opt_in_time",
        "pca_details",
        "phone_alternate",
        "phone_landline",
        "phone_mobile",
        "phone_sms_allowed",
        "phone_ttd",
        "physician_name",
        "physician_phone",
        "pickup_delay",
        "reservation_note",
        "title",
        "vehicle_req",
        "ssn_encrypted",
        "accounts.amount",
        "accounts.id",
        "default_device.code",
        "default_device.id",
        "default_device.name",
        "default_provider.id",
        "default_provider.name",
        "home_county.id",
        "home_county.name",
        "home_location.city",
        "home_location.county",
        "home_location.deleted",
        "home_location.id",
        "home_location.name",
        "home_location.number",
        "home_location.postcode",
        "home_location.street",
        "selfservice_profile.id",
        "selfservice_profile.username",
        "home_location.timezone.id",
        "default_device.pickup_modifier",
        "default_device.dropoff_modifier",
      ],
      query: [["equal", "id", parseInt(id, 10)]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 1,
        calculate_total: false,
        return_results: true,
        select_distinct: true
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/customer/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to load client details for ID ${id}`);
    }
  },

  getFundingSources: async (): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: ["id", "name", "fare_rounding", "priority"],
      query: [["equal", "major", true]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 30,
        order: [
          ["name", "asc_nulls_last"],
          ["id", "asc"],
        ],
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/funding_source/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load funding sources");
    }
  },
  getDevices: async (): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: ["id", "name"],
      query: [["and", ["equal", "enabled", true], ["equal", "deleted", false]]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 30,
        order: [
          ["ordering", "asc_nulls_last"],
          ["id", "asc"],
        ],
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/device/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load devices");
    }
  },
  getVehicleRequirements: async (): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: ["id", "name"],
      query: [["equal", "deleted", false]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 1000,
        order: [
          ["name", "asc_nulls_last"],
          ["id", "asc"],
        ],
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/vehicle_requirement/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load vehicle requirements");
    }
  },
  getCounties: async (): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: ["id", "name"],
      query: [["equal", "enabled", true]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 30,
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/customer_county/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load counties");
    }
  },
  getSponsors: async (): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: ["id", "name", "fare_rounding", "priority"],
      query: [["equal", "sponsor", true]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 30,
        order: [
          ["name", "asc_nulls_last"],
          ["id", "asc"],
        ],
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/funding_source/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load sponsors");
    }
  },
  getPurposes: async (): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: ["id", "description", "deleted"],
      query: [["equal", "deleted", false]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 30,
        order: [
          ["description", "asc_nulls_last"],
          ["id", "asc"],
        ],
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/purpose/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load purposes");
    }
  },
  getFareTypes: async (): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: ["id", "display_name", "avt_handle_type"],
      query: [["equal", "allowed_customer", true]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 1000,
        order: [
          ["display_name", "asc_nulls_last"],
          ["id", "asc"],
        ],
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/fare_type/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load fare types");
    }
  },
  getDefaultProviders: async (): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: ["id", "name", "city", "transportation_subcontractor"],
      query: [["null", "deleted"]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 30,
        order: [
          ["mf_area_status", "asc_nulls_last"],
          ["name", "asc_nulls_last"],
        ],
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/company/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load default providers");
    }
  },
  getBillingCodeSetsFiltered: async (billingCodeIds: number[], searchTerm?: string): Promise<ClientDetailsResponse> => {
    const query: unknown[] = [];

    if (billingCodeIds.length > 0) {
      query.push([
        'or',
        ['in', 'billing_codes.additional_passengers_billing_code.id', billingCodeIds],
        ['in', 'billing_codes.client_copay_billing_code.id', billingCodeIds],
        ['in', 'billing_codes.full_fare_billing_code.id', billingCodeIds],
        ['in', 'billing_codes.sponsor_share_billing_code.id', billingCodeIds],
      ]);
    }

    if (searchTerm) {
      query.push(['like', 'name', `%${searchTerm}%`]);
    }

    const requestBody = {
      select: ['id', 'name'],
      query,
      options: {
        timeout: 10,
        offset: 0,
        limit: 1000,
        order: [
          ['name', 'asc_nulls_last'],
          ['id', 'asc'],
        ],
        calculate_total: true,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy('support/internal/billing_code_set/select_query'),
        requestBody
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to load filtered billing code sets');
    }
  },

  getBillingCodeSetsList: async (
    searchTerm?: string,
    billingCodeSetIds: number[] = [],
    page = 0,
    rowsPerPage = 10,
    sortKey = 'name',
    sortDirection = 'ascending',
  ): Promise<ClientDetailsResponse> => {
    const dbSortDir = sortDirection === 'ascending' ? 'asc_nulls_last' : 'desc_nulls_last';

    const query: unknown[] = [];

    if (billingCodeSetIds.length > 0) {
      const idFilters = billingCodeSetIds.map(id => ['equal', 'id', id]);
      query.push(billingCodeSetIds.length === 1 ? idFilters[0] : ['or', ...idFilters]);
    }

    if (searchTerm && searchTerm.trim()) {
      query.push([
        'or',
        ['like', 'name', `%${searchTerm}%`],
        ['like', 'billing_codes.name', `%${searchTerm}%`],
        ['like', 'billing_codes.additional_passengers_billing_code.display_name', `%${searchTerm}%`],
        ['like', 'billing_codes.client_copay_billing_code.display_name', `%${searchTerm}%`],
        ['like', 'billing_codes.full_fare_billing_code.display_name', `%${searchTerm}%`],
        ['like', 'billing_codes.sponsor_share_billing_code.display_name', `%${searchTerm}%`],
      ]);
    }

    const requestBody = {
      select: ['id', 'name'],
      query,
      options: {
        timeout: 10,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        order: [
          [sortKey === 'name' ? 'name' : 'name', dbSortDir],
          ['id', 'asc'],
        ],
        calculate_total: true,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy('support/internal/billing_code_set/select_query'),
        requestBody
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to load billing code sets list');
    }
  },

  getBillingCodeSetsForDropdown: async (): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: [
        "id",
        "name"
      ],
      query: [],
      options: {
        offset: 0,
        limit: 1000,
        calculate_total: false,
        return_results: true,
        select_distinct: false
      }
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy('support/internal/billing_code_set/select_query'),
        requestBody
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to load billing code sets');
    }
  },

  getBillingCodesBySetId: async (billingCodeSetId: number): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: [
        "additional_passengers_billing_code.display_name",
        "additional_passengers_billing_code.id",
        "billing_code_set.id",
        "billing_code_set.name",
        "client_copay_billing_code.display_name",
        "client_copay_billing_code.id",
        "end_date",
        "full_fare_billing_code.display_name",
        "full_fare_billing_code.id",
        "id",
        "name",
        "sponsor_share_billing_code.display_name",
        "sponsor_share_billing_code.id",
        "start_date",
        "valid_fri",
        "valid_from_time",
        "valid_mon",
        "valid_sat",
        "valid_sun",
        "valid_thu",
        "valid_tue",
        "valid_until_time",
        "valid_wed"
      ],
      query: [
        ["equal", "billing_code_set.id", billingCodeSetId]
      ],
      options: {
        offset: 0,
        limit: 1000,
        calculate_total: false,
        return_results: true,
        select_distinct: false,
        order: [["name", "asc"]],
        timeout: 20000
      }
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy('support/internal/billing_code/select_query'),
        requestBody
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to load billing codes for set ID ${billingCodeSetId}`);
    }
  },

  getBillingCodesBySetIds: async (
    billingCodeSetIds: number[],
    searchTerm?: string,
    page = 0,
    rowsPerPage = 10,
    sortKey = 'name',
    sortDirection = 'ascending',
  ): Promise<ClientDetailsResponse> => {
    // Map ViewModel field names to DB column names for server-side sorting
    const sortFieldMap: Record<string, string> = {
      name: 'name',
      rule: 'name',
      fullFare: 'full_fare_billing_code.display_name',
      clientCopay: 'client_copay_billing_code.display_name',
      sponsorShare: 'sponsor_share_billing_code.display_name',
      additionalPassenger: 'additional_passengers_billing_code.display_name',
      startEnd: 'start_date',
      endDate: 'end_date',
      fromTime: 'valid_from_time',
      toTime: 'valid_until_time',
    };
    const dbSortField = sortFieldMap[sortKey] ?? 'billing_code_set.name';
    const dbSortDir = sortDirection === 'ascending' ? 'asc_nulls_last' : 'desc_nulls_last';

    let query: unknown[] = [];

    if (billingCodeSetIds.length > 0) {
      const idFilters = billingCodeSetIds.map(id => ["equal", "billing_code_set.id", id]);
      query.push(billingCodeSetIds.length === 1 ? idFilters[0] : ["or", ...idFilters]);
    }

    if (searchTerm) {
      const searchFilter = ["or",
        ["like", "name", `%${searchTerm}%`],
        ["like", "billing_code_set.name", `%${searchTerm}%`],
        ["like", "full_fare_billing_code.display_name", `%${searchTerm}%`],
        ["like", "client_copay_billing_code.display_name", `%${searchTerm}%`],
        ["like", "sponsor_share_billing_code.display_name", `%${searchTerm}%`],
        ["like", "additional_passengers_billing_code.display_name", `%${searchTerm}%`]
      ];
      if (query.length > 0) {
        query = [["and", query, searchFilter]];
      } else {
        query = searchFilter;
      }
    }

    const requestBody = {
      select: [
        "id",
        "name",
        "billing_code_set.id",
        "billing_code_set.name",
        "additional_passengers_billing_code.id",
        "additional_passengers_billing_code.display_name",
        "client_copay_billing_code.id",
        "client_copay_billing_code.display_name",
        "full_fare_billing_code.id",
        "full_fare_billing_code.display_name",
        "sponsor_share_billing_code.id",
        "sponsor_share_billing_code.display_name",
        "start_date",
        "end_date",
        "valid_from_time",
        "valid_until_time",
        "valid_sun",
        "valid_mon",
        "valid_tue",
        "valid_wed",
        "valid_thu",
        "valid_fri",
        "valid_sat",
        "company.id",
        "company.name",
      ],
      query,
      options: {
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        calculate_total: true,
        return_results: true,
        select_distinct: false,
        order: [[dbSortField, dbSortDir],["valid_from_time","asc_nulls_last"],["valid_until_time","asc_nulls_last"],["id","asc"]],
        timeout: 20000,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy('support/internal/billing_code/select_query'),
        requestBody
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to load billing codes`);
    }
  },

  getClientBalance: async (clientId: string): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: [
        "id",
        "name",
        "enabled",
        "customer_number",
        "accounts.amount",
        "accounts.id"
      ],
      query: [["equal", "id", parseInt(clientId, 10)]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 1,
        calculate_total: false,
        return_results: true,
        select_distinct: true
      }
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/customer/select_query"),
        requestBody
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to load client balance for ID ${clientId}`);
    }
  },

  getClientTransactions: async (accountId: string, page: number, limit: number): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: [
        "agent",
        "amount",
        "comment",
        "customer_account_id",
        "customer_external_payment_method_ref",
        "id",
        "timestamp",
        "trip_origin_id",
        "trip_tracking_info",
        "type",
        "uuid"
      ],
      query: [
        ["equal", "customer_account_id", parseInt(accountId, 10)],
        ["in", "type", ["MANUAL", "CORRECTION", "PAYMENT"]]
      ],
      options: {
        timeout: 10,
        offset: page * limit,
        limit: limit,
        order: [
          ["timestamp", "desc_nulls_last"],
          ["uuid", "asc_nulls_last"]
        ],
        calculate_total: true,
        return_results: true,
        select_distinct: true
      }
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/archive_customer_account_transaction/select_query"),
        requestBody
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to load transactions for account ${accountId}`);
    }
  },

  adjustClientBalance: async (
    customerAccountId: string,
    agent: string,
    amount: number,
    comment: string
  ): Promise<ClientDetailsResponse> => {
    // Convert dollars to cents, ensuring it's a valid integer
    const amountInCents = Math.round(amount * 100);
    
    const requestBody = {
      customer_account_id: parseInt(customerAccountId, 10),
      agent: agent,
      amount: amountInCents,
      comment: comment || "", // Ensure comment is never null/undefined
      type: "MANUAL"
    };

    let createResponse: ClientDetailsResponse;
    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/customer_account_transaction"),
        requestBody
      );
      createResponse = response.data;
    } catch (error: unknown) {
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      throw new Error(msg || `Failed to adjust balance`);
    }

    // Mirrors Rails Administration::CustomerAccountTransactionsController#sync_transactions:
    // immediately archive the newly-created transaction so it appears in the
    // archive_customer_account_transaction table (which backs the balance tab).
    // Falls back to archive_all_async when the per-record archive call fails.
    const transactionId =
      (createResponse as unknown as { id?: unknown })?.id ??
      (createResponse as unknown as { resource_id?: unknown })?.resource_id ??
      (createResponse as unknown as { successes?: Array<{ resource_id?: unknown }> })
        ?.successes?.[0]?.resource_id;

    if (transactionId != null) {
      try {
        await apiClient.post(
          apiRoutes.proxy(`support/internal/customer_account_transaction/${transactionId}/archive`)
        );
      } catch (archiveError) {
        logger.error(
          `Failed to archive transaction ${transactionId}; falling back to archive_all_async:`,
          archiveError,
        );
        // Fallback: trigger async archival of all pending transactions
        try {
          await apiClient.post(
            apiRoutes.proxy("support/internal/customer_account_transaction/archive_all_async")
          );
        } catch (fallbackError) {
          // Fallback also failed — balance table will update on the next
          // background archive job run. Non-fatal: don't block the caller.
          logger.error(
            "archive_all_async fallback also failed; balance table will sync on next background job run:",
            fallbackError,
          );
        }
      }
    }

    return createResponse;
  },

  getClientLocations: async (
    customerId: string,
    page = 0,
    limit = 10,
    order: [string, string][] = [['name', 'asc_nulls_last'], ['id', 'asc']],
  ): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: [
        "cc_notes",
        "city",
        "county",
        "driver_notes",
        "id",
        "location_name",
        "name",
        "phone",
        "postcode",
        "state",
        "street",
        "street_number",
        "suffix",
        "valid"
      ],
      query: [
        ["equal", "customer.id", parseInt(customerId, 10)],
        ["equal", "valid", true]
      ],
      options: {
        timeout: 10,
        offset: page * limit,
        limit,
        order,
        calculate_total: true,
        return_results: true,
        select_distinct: true
      }
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/customer_poi/select_query"),
        requestBody
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to load client locations`);
    }
  },

  encryptSsn: async (plaintext: string): Promise<string | null> => {
    try {
      const response = await apiClient.post<{ data: Array<{ encrypted: string; ssn: string }> }>(
        apiRoutes.proxy("support/internal/customer/encrypt_ssn"),
        { data: [plaintext] }
      );
      return response.data?.data?.[0]?.encrypted ?? null;
    } catch (error) {
      throw new Error("Failed to encrypt SSN");
    }
  },

  createClient: async (clientData: Record<string, unknown>): Promise<ClientDetailsResponse> => {
    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/customer"),
        clientData
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to create client");
    }
  },

  addClientLocation: async (locationData: {
    name: string;
    city: string;
    county: string | null;
    phone: string;
    postcode: string | null;
    state: string;
    street: string;
    street_number: string | null;
    valid: boolean;
    customer: { id: number };
    location_name: string | null;
    cc_notes: string | null;
    driver_notes: string | null;
    suffix: string | null;
  }): Promise<ClientDetailsResponse> => {
    try {
      const response = await apiClient.post<ClientDetailsResponse>(apiRoutes.proxy("support/internal/customer_poi"),
        locationData
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to add client location");
    }
  },

  getClientsByAccountIds: async (
    accountIds: number[],
    page: number,
    limit: number,
    query: unknown[],
    order: unknown[],
  ): Promise<ClientDetailsResponse> => {
    // Add the account IDs filter to the query
    const modifiedQuery = [
      ...query,
      ["in", "accounts.id", accountIds]
    ];

    const requestBody = {
      select: [
        "id",
        "customer_number",
        "name",
        "enabled",
        "medicaid_number",
        "medicare_number",
        "ssn_encrypted",
        "date_of_birth",
        "gender",
        "language_code",
        "email",
        "phone_mobile",
        "phone_landline",
        "vehicle_req",
        "pickup_delay",
        "dropoff_delay",
        "last_ride_timestamp",
        "creation_ts",
        "home_street_number",
        "home_street",
        "home_street_suffix",
        "home_city",
        "home_postcode",
        "default_device.id",
        "default_device.name",
        "default_device.code",
        "default_provider.id",
        "home_location.id",
        "selfservice_profile.id",
        "selfservice_profile.status",
      ],
      query: modifiedQuery,
      options: {
        timeout: 10,
        offset: page * limit,
        limit: limit,
        order,
        calculate_total: true,
        return_results: true,
        select_distinct: true,
      },
    };


    try {
      const response = await apiClient.post<ClientDetailsResponse>(apiRoutes.proxy("support/internal/customer/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load clients by account IDs");
    }
  },

  getClientsWithoutSelfServiceProfile: async (offset = 0, searchTerm = ''): Promise<ClientDetailsResponse> => {
    const PAGE_SIZE = 30;
    const query: unknown[][] = [
      ["equal", "enabled", "ENABLED"],
      ["null", "selfservice_profile"],
    ];
    if (searchTerm.trim()) {
      query.push(["or",
        ["like", "name", `%${searchTerm.trim()}%`],
        ["like", "customer_number", `%${searchTerm.trim()}%`],
      ]);
    }

    const requestBody = {
      select: ["id", "name", "enabled", "customer_number", "email"],
      query,
      options: {
        timeout: 10,
        offset,
        limit: PAGE_SIZE,
        order: [
          ["name", "asc_nulls_last"],
          ["customer_number", "asc_nulls_last"],
          ["id", "asc"],
        ],
        calculate_total: true,
        return_results: true,
        select_distinct: true,
      },
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy("support/internal/customer/select_query"),
        requestBody,
      );
      return response.data;
    } catch (error) {
      throw new Error("Failed to load clients without self-service profile");
    }
  },

  /**
   * Fetch the customer_profile_picture.id for a customer.
   *
   * Mirrors the RoR qm_form_tab which loads the customer with
   * `[:customer_profile_picture, :_ref]`. We replicate this by querying
   * the customer record with `customer_profile_picture.id` in the select.
   *
   * The returned id is used directly for:
   *   GET    /customer_profile_picture/{id}/customer/{customerId}
   *   DELETE /customer_profile_picture/{id}/customer/{customerId}
   */
  getClientProfilePicture: async (customerId: string): Promise<{ id: number } | null> => {
    /** Stable select field list — drives both the API request and the index map below. */
    const PROFILE_PIC_SELECT = ['id', 'customer_profile_picture.id'] as const;
    /**
     * Stable name→index map so row access never uses magic positional numbers.
     * Usage: row[profilePicIdx['customer_profile_picture.id']]
     */
    const profilePicIdx = Object.fromEntries(
      PROFILE_PIC_SELECT.map((f, i) => [f, i])
    ) as Record<typeof PROFILE_PIC_SELECT[number], number>;

    const requestBody = {
      select: [...PROFILE_PIC_SELECT],
      query: [['equal', 'id', parseInt(customerId, 10)]],
      options: {
        timeout: 10,
        offset: 0,
        limit: 1,
        calculate_total: false,
        return_results: true,
        select_distinct: true,
      },
    };
    try {
      const response = await apiClient.post<ClientDetailsResponse>(
        apiRoutes.proxy('support/internal/customer/select_query'),
        requestBody,
      );
      // K3 select_query rows come back as (string | number | null)[][] after the
      // responseTransformer runs. ClientDetailsResponse types the envelope but
      // does not expose `.data` directly, so we cast to the known row shape.
      const rows = (response.data as { data?: (string | number | null)[][] })?.data;
      if (!rows || rows.length === 0) return null;
      const row = rows[0];
      // Row comes back as array tuple or as object { id, customer_profile_picture: { id } }
      let pictureId: number;
      if (Array.isArray(row)) {
        pictureId = Number(row[profilePicIdx['customer_profile_picture.id']]);
      } else {
        pictureId = Number(
          (row as unknown as { customer_profile_picture?: { id?: number } })
            ?.customer_profile_picture?.id
        );
      }
      if (!pictureId) return null;
      return { id: pictureId };
    } catch {
      return null;
    }
  },

  /**
   * Fetch the profile photo image blob via the RoR BFF download gateway.
   * Endpoint: GET /api/download/customer_profile_picture/{pictureId}/customer/{customerId}
   *
   * Uses apiRoutes.download (not proxy) because:
   *   - gateway#proxy sends Accept: application/json to K3, which RESTEasy rejects for binary
   *   - gateway#download streams bytes from K3 without JSON conversion (correct for binary)
   */
  getClientProfilePhoto: async (pictureId: number, customerId: string): Promise<Blob | null> => {
    try {
      const response = await apiClient.get(
        apiRoutes.download(`support/internal/customer_profile_picture/${pictureId}/customer/${customerId}`),
        {
          responseType: 'blob',
          skipApiFailureHandling: true,
        },
      );
      return response.data as Blob;
    } catch {
      return null;
    }
  },

  /**
   * Upload a profile photo for a customer via the RoR BFF upload proxy.
   * Endpoint: POST /support/internal/customer_profile_picture/{customerId}/file
   * Sends multipart/form-data; Content-Type boundary is set automatically by axios.
   * Returns the picture record id from the response for immediate cache population.
   */
  uploadClientProfilePhoto: async (customerId: string, file: File): Promise<{ pictureId: number | null }> => {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await apiClient.post(
      apiRoutes.upload(`support/internal/customer_profile_picture/${customerId}/file`),
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        skipApiFailureHandling: true,
      },
    );
    // K3 upload responses have no fixed schema — the shape varies between
    // success (may contain id/resource_id/successes) and failure objects.
    // We use unknown + a narrowing interface rather than any.
    type K3UploadResponse = {
      failure?: boolean;
      success?: boolean;
      message?: string;
      errors?: Array<{ message?: string }>;
      id?: unknown;
      resource_id?: unknown;
      successes?: Array<{ resource_id?: unknown }>;
    };
    const data = response.data as K3UploadResponse;
    if (data?.failure || data?.success === false) {
      const backendMessage = data?.errors?.[0]?.message || data?.message || 'Upload failed';
      throw new Error(backendMessage);
    }
    // Extract the picture record id from the response if available
    const rawId =
      data?.id ??
      data?.resource_id ??
      data?.successes?.[0]?.resource_id ??
      null;
    return { pictureId: rawId != null ? Number(rawId) : null };
  },

  /**
   * Hard-delete the profile photo for a customer via the RoR BFF proxy.
   * Endpoint: DELETE /support/internal/customer_profile_picture/{pictureId}/customer/{customerId}
   */
  deleteClientProfilePhoto: async (pictureId: number, customerId: string): Promise<void> => {
    try {
      await apiClient.delete(
        apiRoutes.proxy(`support/internal/customer_profile_picture/${pictureId}/customer/${customerId}`),
        { skipApiFailureHandling: true },
      );
    } catch (error: unknown) {
      // Treat 404 as success — the record is already gone.
      // We narrow to unknown and check via the Axios error shape.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) return;
      throw error;
    }
  },

  getCustomerFullDetails: async (customerId: number): Promise<ClientDetailsResponse> => {
    const requestBody = {
      select: [
        "id",
        "travel_rights"
      ],
      options: {
        timeout: 10,
        offset: 0,
        limit: 1,
        calculate_total: false,
        return_results: true,
        select_distinct: true
      },
      query: [["in", "id", [customerId]]]
    };

    try {
      const response = await apiClient.post<ClientDetailsResponse>(apiRoutes.proxy("support/internal/customer/select_query"),
        requestBody
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to load customer full details for ID ${customerId}`);
    }
  },
});

export type ClientsApi = ReturnType<typeof createClientsApi>;
